import { useState } from "react";
import { X, Facebook, Twitter, Linkedin, Link, Copy, Check } from "lucide-react";

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  thumbnail?: string;
  url: string;
}

const ShareModal = ({ isOpen, onClose, title, thumbnail, url }: ShareModalProps) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");

  const shareOptions = [

    {
      name: "Facebook",
      icon: Facebook,
      color: "bg-blue-600",
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    },
    {
      name: "X",
      icon: Twitter,
      color: "bg-black",
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(shareUrl)}`,
    },
    {
      name: "LinkedIn",
      icon: Linkedin,
      color: "bg-blue-700",
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    },
    {
      name: "WhatsApp",
      icon: () => (
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.964-.94 1.162-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.148-.267-1.563-.366-2.093-.099-.53-.173-.916-.249-1.195-.074-.279-.025-.595.05-.743l.497-.622c.21-.273.25-.437.346-.548l.495-.398c.272-.174.447-.223.612-.223.174 0 .348.025.498.124.149.1.271.272.372.397l.422.496c.126.224.173.37.272.496.099.124.174.273.273.372l.347.199c.124.074.249.124.397.124.174 0 .372-.025.546-.173.174-.149.745-.595 1.36-1.472.614-.876.975-1.497 1.11-1.795.149-.298.149-.496.124-.62-.025-.124-.124-.198-.273-.347l-.273-.174c-.124-.099-.174-.174-.272-.348-.099-.174-.025-.347.05-.52.124-.272.372-.595.546-.795.174-.199.347-.372.546-.496.198-.124.272-.174.397-.249l.52-.298c.124-.074.198-.124.273-.124h.124c.124 0 .272.025.422.124.149.099.347.272.422.397.074.124.149.198.173.322-.025.124-.025.248 0 .372 0 .124-.025.248-.025.347v.496c-.025.124-.025.248-.025.347-.025.124-.074.248-.124.372-.099.124-.124.199-.124.272l-.025.025c-.099.124-.124.199-.124.272l-.025.025c-.124.124-.248.272-.372.422-.124.149-.199.272-.299.396-.099.124-.149.198-.248.298l-.025.025c-.124.099-.248.199-.347.298-.099.099-.173.173-.272.247-.099.074-.173.124-.248.173-.074.05-.124.074-.173.124l-.025.025c-.099.074-.173.124-.248.173-.074.05-.124.074-.173.124-.272.174-.52.272-.77.347l-.025.025c-.248.074-.47.124-.695.173-.224.05-.423.074-.596.025zM12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.82 2 5.08 5.08 2 8.82 2c3.5 0 6.64 3.12 8.63 8.55l-1.45 3.52c-.3.73-.95 1.25-1.72 1.25h-2.88c-.53 0-1.04-.27-1.32-.69-.55-.83-.83-1.78-.83-2.78 0-2.26 1.84-4.1 4.1-4.1 2.26 0 4.1 1.84 4.1 4.1 0 2.26-1.84 4.1-4.1 4.1-.53 0-1.04-.15-1.49-.42l-.025.025c-.124.1-.248.174-.347.174zm4.99-7.65c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-7.49 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/>
        </svg>
      ),
      color: "bg-green-500",
      url: `https://wa.me/?text=${encodeURIComponent(title + "\n" + shareUrl)}`,
    },
    {
      name: "More",
      icon: Link,
      color: "bg-gray-600",
      url: null,
    },
  ];

  const handleShare = (option: typeof shareOptions[0]) => {
    if (option.name === "More") {
      if (navigator.share) {
        navigator.share({
          title: title,
          text: title,
          url: shareUrl,
        });
      } else {
        navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
      return;
    }
    if (option.url) {
      window.open(option.url, "_blank", "width=600,height=400");
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Share Course</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4">
          {thumbnail && (
            <img
              src={thumbnail}
              alt={title}
              className="w-full h-32 object-cover rounded-lg mb-4"
            />
          )}
          <p className="font-medium text-gray-900 mb-4 line-clamp-2">{title}</p>

          <div className="grid grid-cols-5 gap-2">
            {shareOptions.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.name}
                  onClick={() => handleShare(option)}
                  className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className={`${option.color} w-10 h-10 rounded-full flex items-center justify-center text-white`}>
                    <Icon />
                  </div>
                  <span className="text-[10px] text-gray-600">{option.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={shareUrl}
              className="flex-1 px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-xs text-gray-600 truncate"
            />
            <button
              onClick={handleCopy}
              className="p-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          {copied && <p className="text-xs text-green-600 mt-2">Link copied to clipboard!</p>}
        </div>
      </div>
    </div>
  );
};

export default ShareModal;